import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ISession } from '@models';
import {
    ODataFilterProperty,
    ODataQueryOptions,
} from '@services/generics/odata.query';
import {
    IODataArray,
    ODATA_COUNT_KEY,
    ODataService,
} from '@services/generics/odata.service';
import { MapperService } from '@services/mapper.service';

import { map } from 'rxjs/operators';

@Injectable()
export class SessionsService extends ODataService<ISession, number> {
    protected _endpoint = '/odata/Sessions';
    constructor(http: HttpClient) {
        super(http, MapperService.sessionMap);
    }

    public getSessionsForEnvironment(environmentId: number, queryOptions: ODataQueryOptions) {
        if (
            queryOptions.expand instanceof Array &&
            !queryOptions.expand.includes('Robot')
        ) {
            queryOptions.expand.push('Robot');
        } else {
            queryOptions.expand = ['Robot'];
        }

        Object.assign(queryOptions.filter, {
            Robot: {
                Environments: {
                    any: {
                        Id: environmentId,
                    },
                },
                Type: {
                    ne: 'Attended',
                },
            },
        });
        return this.get(queryOptions);
    }

    public getNonAttendedRobotInEnvironment(environmentId: number, robotId: number) {
        const queryOptions: ODataQueryOptions = {
            expand: ['Robot'],
            filter: {
                Robot: {
                    Environments: {
                        any: {
                            Id: environmentId,
                        },
                    },
                    Id: {
                        eq: robotId,
                    },
                    Type: {
                        ne: 'Attended',
                    },
                },
            },
        };

        return this.get(queryOptions);
    }

    public suggest = (searchTerm: string = '', fetchSize = 20, skip = 0) => {
        const queryOptions = {
            ...this._getSuggestOptions('Name', searchTerm, fetchSize, skip),
            expand: ['Robot'],
            select: ['Robot'],
            orderBy: [`Robot/Name`],
        };

        return this.get(queryOptions)
            .pipe(
                map(
                    ({ data, total }) =>
                        ({
                            total,
                            data: data.map(session => ({
                                id: session.robot.id,
                                text: session.robot.name,
                            })),
                        }),
                ),
            );
    }

    public getByMachineId(machineId: number, queryOptions: ODataQueryOptions) {
        queryOptions.expand = ['Robot'];
        (queryOptions.filter as ODataFilterProperty)['MachineId'] = machineId;

        return this.get(queryOptions);
    }

    public userNameSuggest = (searchTerm: string = '', fetchSize = 20, skip = 0) => {
        const queryOptions = this._getSuggestOptions('Username', searchTerm, fetchSize, skip);
        const endpoint = `${this._endpoint}/UiPath.Server.Configuration.OData.GetUsernames${this.getQueryString(queryOptions)}`;

        return this._http
            .get<IODataArray<string>>(endpoint)
            .pipe(
                map(
                    res => ({
                        data: res.value.map(
                            userName => ({
                                id: userName,
                                text: userName,
                            }),
                        ),
                        total: res[ODATA_COUNT_KEY],
                    }),
                ),
            );
    }

    public robotsByUsernameSuggest = (searchTerm: string = '', fetchSize = 20, skip = 0) => {
        const queryOptions = this._getSuggestOptions('Username', searchTerm, fetchSize, skip);
        const endpoint = `${this._endpoint}/UiPath.Server.Configuration.OData.GetUserRobots${this.getQueryString(queryOptions)}`;

        return this._http
            .get<IODataArray<{ UserName: string, RobotNames: string[] }>>(endpoint)
            .pipe(
                map(
                    res => ({
                        data: res.value.map(
                            ({ UserName, RobotNames }) => ({
                                id: RobotNames,
                                text: UserName,
                            }),
                        ),
                        total: res[ODATA_COUNT_KEY],
                    }),
                ),
            );
    }

    private _getSuggestOptions(property = 'Name', searchTerm: string = '', fetchSize = 20, skip = 0) {
        return {
            top: fetchSize,
            skip,
            filter: {
                Robot: {
                    [property]: {
                        contains: searchTerm,
                    },
                },
            },
        };
    }
}
